import * as Sentry from "@sentry/react";

/**
 * Request notification permission from the user
 * Works on Windows 10, Windows 11, and macOS
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!("Notification" in window)) {
    console.warn("This browser does not support desktop notifications");
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    try {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }

  return false;
};

/**
 * Check if notifications are supported and permitted
 */
export const canShowNotifications = (): boolean => {
  return (
    "Notification" in window && Notification.permission === "granted"
  );
};

/**
 * Show a system tray notification
 * Compatible with Windows 10, Windows 11, and macOS
 */
export const showSystemNotification = (
  title: string,
  options?: {
    body?: string;
    icon?: string;
    badge?: string;
    tag?: string;
    data?: any;
    requireInteraction?: boolean;
    silent?: boolean;
  }
): Notification | null => {
  if (!canShowNotifications()) {
    return null;
  }

  try {
    const notification = new Notification(title, {
      body: options?.body || "",
      icon: options?.icon || "/src/favicon.png",
      badge: options?.badge || "/src/favicon.png",
      tag: options?.tag || `notification-${Date.now()}`,
      data: options?.data || {},
      requireInteraction: options?.requireInteraction || false,
      silent: options?.silent || false,
    });

    return notification;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error showing system notification:", error);
    return null;
  }
};

/**
 * Show an order notification in the system tray
 */
export const showOrderNotification = (
  title: string,
  body: string,
  orderData: {
    orderId?: number;
    externalOrderId?: string;
    shopId?: string;
    type?: string;
    subType?: string;
    url?: string;
    customerName?: string;
    customerPhone?: string;
    orderTotal?: string;
  }
): Notification | null => {
  // Build notification body with customer information if available
  const details: string[] = [];
  
  if (orderData.customerName) {
    details.push(`Customer: ${orderData.customerName}`);
  }
  
  if (orderData.customerPhone) {
    details.push(`Phone: ${orderData.customerPhone}`);
  }
  
  if (orderData.orderTotal) {
    // orderTotal is already formatted with ₹ symbol from formatPrice
    details.push(`Total: ${orderData.orderTotal}`);
  }
  
  // Construct final body: use provided body if exists, otherwise use details only
  let enhancedBody = "";
  if (body && body.trim()) {
    enhancedBody = body;
    if (details.length > 0) {
      enhancedBody += `\n\n${details.join('\n')}`;
    }
  } else if (details.length > 0) {
    enhancedBody = details.join('\n');
  }
  
  return showSystemNotification(title, {
    body: enhancedBody,
    tag: `order-${orderData.externalOrderId || orderData.orderId || Date.now()}`,
    data: orderData,
    requireInteraction: true, // Keep notification visible until user interacts
    icon: "/src/favicon.png",
  });
};

/**
 * Get the current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (!("Notification" in window)) {
    return "denied";
  }
  return Notification.permission;
};
